'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import {
  getEffectiveOperationsActionPermission,
  type OperationsActionKey,
} from '@/lib/operations/actionPermissions';
import effectivePermissionsStore from '@/lib/offline/effectivePermissionsStore';

// ============================================================================
// TYPES
// ============================================================================

export interface EffectivePermissionsState {
  /** Flat snake_case key→value permissions (same shape as my-features feature_settings) */
  permissions: Record<string, unknown>;
  /** User's role for this module */
  role: string;
  /** Whether the user has access to this module */
  hasAccess: boolean;
  /** Whether permissions are still loading */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Provenance: which source each permission key was resolved from */
  resolvedVia: Record<string, string>;
  /** Just the per-user override portion (custom_permissions + legacy fallbacks) */
  overridesOnly: Record<string, unknown>;
  /** Whether the user is a field worker */
  isFieldWorker: boolean;
  /** Whether the user is a root user (all permissions true via bypass) */
  isRoot: boolean;
  /**
   * Feature rows owned by each industry-solution parent group, keyed by
   * source_solution_key. Overlay the active solution's slice onto
   * `permissions` for context-scoped resolution.
   */
  solutionPermissions: Record<string, Record<string, unknown>>;
}

interface EffectivePermissionsApiResponse {
  success: boolean;
  has_access: boolean;
  role?: string;
  permissions?: Record<string, unknown>;
  resolved_via?: Record<string, string>;
  overrides_only?: Record<string, unknown>;
  is_field_worker?: boolean;
  is_root?: boolean;
  device_compliance?: unknown;
  solution_permissions?: Record<string, Record<string, unknown>>;
  error?: string;
}

// ============================================================================
// CACHE — per module_key, 30s TTL (matches my-features caching)
// ============================================================================

const CACHE_TTL_MS = 30 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<EffectivePermissionsApiResponse | null> | null;
  value: EffectivePermissionsApiResponse | null;
}

const permissionsCache = new Map<string, CacheEntry>();

function getCacheKey(accessToken: string, moduleKey: string): string {
  const clientId = typeof window !== 'undefined'
    ? window.localStorage.getItem('current_client_id') || 'no-client'
    : 'no-client';
  return `${accessToken}:${clientId}:${moduleKey}`;
}

function getClientId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem('current_client_id');
  const parsed = raw ? parseInt(raw, 10) : null;
  return parsed && Number.isFinite(parsed) ? parsed : null;
}

// ============================================================================
// FETCH
// ============================================================================

function buildEffectivePermissionsPath(moduleKey: string): string {
  const params = new URLSearchParams();
  params.set('module_key', moduleKey);

  if (typeof window !== 'undefined') {
    const clientId = window.localStorage.getItem('current_client_id');
    if (clientId) {
      params.set('client_hint', clientId);
    }
  }

  params.set('_ts', String(Date.now()));
  return `/api/v1/iam/effective-permissions?${params.toString()}`;
}

async function fetchEffectivePermissions(
  accessToken: string,
  moduleKey: string,
  options: { forceRefresh?: boolean; timeoutMs?: number } = {},
): Promise<EffectivePermissionsApiResponse | null> {
  if (!accessToken) return null;

  const cacheKey = getCacheKey(accessToken, moduleKey);
  const now = Date.now();
  const cached = permissionsCache.get(cacheKey);

  if (!options.forceRefresh && cached) {
    if (cached.promise) return cached.promise;
    if (cached.expiresAt > now && cached.value !== null) return cached.value;
  }

  const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutMs = Math.max(1_000, options.timeoutMs ?? 8_000);
  const timeoutId = abortController
    ? window.setTimeout(() => {
      try {
        abortController.abort(new DOMException(`effective-permissions exceeded ${timeoutMs}ms`, 'TimeoutError'));
      } catch {
        abortController.abort();
      }
    }, timeoutMs)
    : null;

  const requestPromise = fetch(
    getApiUrl(buildEffectivePermissionsPath(moduleKey)),
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      credentials: 'include',
      signal: abortController?.signal,
    },
  )
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`effective-permissions returned ${response.status}`);
      }
      return response.json() as Promise<EffectivePermissionsApiResponse>;
    })
    .finally(() => {
      if (timeoutId) window.clearTimeout(timeoutId);
    });

  // Cache the promise so concurrent callers share it
  const entry: CacheEntry = {
    expiresAt: now + CACHE_TTL_MS,
    promise: requestPromise,
    value: null,
  };
  permissionsCache.set(cacheKey, entry);

  try {
    const result = await requestPromise;
    entry.promise = null;
    entry.value = result;

    // Persist to offline store for offline access
    if (result?.success) {
      const clientId = getClientId();
      if (clientId) {
        effectivePermissionsStore.save(clientId, moduleKey, {
          module_key: moduleKey,
          permissions: result.permissions || {},
          role: result.role || 'user',
          has_access: result.has_access ?? false,
          resolved_via: result.resolved_via || {},
          overrides_only: result.overrides_only || {},
          is_field_worker: result.is_field_worker ?? false,
          is_root: result.is_root ?? false,
          solution_permissions: result.solution_permissions || {},
        }).catch(() => {
          // Offline store write failure is non-fatal — in-memory cache still works
        });
      }
    }

    return result;
  } catch (error) {
    entry.promise = null;
    // Don't cache errors
    permissionsCache.delete(cacheKey);
    throw error;
  }
}

/**
 * Load cached effective permissions from the offline IndexedDB store.
 * Returns null if no cached data exists.
 */
async function loadOfflinePermissions(moduleKey: string): Promise<EffectivePermissionsState | null> {
  const clientId = getClientId();
  if (!clientId) return null;

  try {
    const stored = await effectivePermissionsStore.load(clientId, moduleKey);
    if (!stored) return null;

    return {
      permissions: stored.permissions,
      role: stored.role,
      hasAccess: stored.has_access,
      isLoading: false,
      error: null,
      resolvedVia: stored.resolved_via,
      overridesOnly: stored.overrides_only,
      isFieldWorker: stored.is_field_worker,
      isRoot: stored.is_root,
      solutionPermissions: (stored as { solution_permissions?: Record<string, Record<string, unknown>> }).solution_permissions || {},
    };
  } catch {
    return null;
  }
}

// ============================================================================
// HOOK
// ============================================================================

const INITIAL_STATE: EffectivePermissionsState = {
  permissions: {},
  role: 'user',
  hasAccess: false,
  isLoading: true,
  error: null,
  resolvedVia: {},
  overridesOnly: {},
  isFieldWorker: false,
  isRoot: false,
  solutionPermissions: {},
};

/**
 * Custom event name broadcast when effective permissions change (e.g. via
 * WebSocket IAM channel, or after a PermissionsSlideOver save). All
 * `useEffectivePermissions` hooks listen for this and re-fetch.
 */
export const IAM_PERMISSIONS_CHANGED_EVENT = 'iam:permissions-changed';

/**
 * Single hook for effective permissions — replaces both
 * `useWorkflowActionPermissions()` and the `configResolver.features` path.
 *
 * Calls `/api/v1/iam/effective-permissions` and caches per module_key.
 *
 * Offline behavior:
 *   - On mount, loads from the offline IndexedDB store first (instant) so
 *     the UI renders immediately even when offline.
 *   - If online, revalidates from the API and updates the offline store.
 *   - If the API fetch fails (offline), keeps using the offline data.
 *
 * @param moduleKey - Module to resolve permissions for (default: 'total-operations')
 */
export function useEffectivePermissions(moduleKey = 'total-operations'): EffectivePermissionsState & {
  can: (permissionKey: string) => boolean;
  canAction: (itemType: string, action: OperationsActionKey, workflowKey?: string | null) => boolean;
  refresh: () => void;
} {
  const [state, setState] = useState<EffectivePermissionsState>(INITIAL_STATE);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [offlineLoaded, setOfflineLoaded] = useState(false);
  const moduleKeyRef = useRef(moduleKey);
  moduleKeyRef.current = moduleKey;

  // ── Load from offline store on mount (instant) ────────────────────────
  // This runs once per moduleKey so the UI can render immediately even
  // when offline, before the API fetch completes or fails.
  useEffect(() => {
    let cancelled = false;

    const loadOffline = async () => {
      const offline = await loadOfflinePermissions(moduleKey);
      if (cancelled) return;
      if (offline) {
        setState(offline);
      }
      setOfflineLoaded(true);
    };

    loadOffline();
    return () => {
      cancelled = true;
    };
  }, [moduleKey]);

  // ── Listen for IAM permission change events (WebSocket realtime) ────────
  // When the WebSocket IAM channel receives a permission change event,
  // it dispatches IAM_PERMISSIONS_CHANGED_EVENT. We catch it here and
  // bump the refresh counter to trigger a re-fetch.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ moduleKey?: string }>).detail;
      // Only refresh if the event is for this module (or global)
      if (!detail?.moduleKey || detail.moduleKey === moduleKey) {
        setRefreshCounter((c) => c + 1);
      }
    };

    window.addEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handler);
    };
  }, [moduleKey]);

  // ── Fetch from API (revalidate) ────────────────────────────────────────
  // Runs after the offline load completes (or on refresh). If the fetch
  // fails, the offline data remains in state — no error overwrite.
  useEffect(() => {
    if (!offlineLoaded && refreshCounter === 0) return;

    let cancelled = false;

    const load = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          if (!cancelled) {
            setState((prev) => ({ ...INITIAL_STATE, isLoading: false }));
          }
          return;
        }

        const data = await fetchEffectivePermissions(token, moduleKeyRef.current);
        if (cancelled) return;

        if (!data || !data.success) {
          // API failed — keep offline data if we have it
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: data?.error || 'Failed to load permissions',
          }));
          return;
        }

        setState({
          permissions: data.permissions || {},
          role: data.role || 'user',
          hasAccess: data.has_access ?? false,
          isLoading: false,
          error: null,
          resolvedVia: data.resolved_via || {},
          overridesOnly: data.overrides_only || {},
          isFieldWorker: data.is_field_worker ?? false,
          isRoot: data.is_root ?? false,
          solutionPermissions: data.solution_permissions || {},
        });
      } catch {
        // Network error (offline) — keep offline data if we have it
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Failed to load permissions',
          }));
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [moduleKey, refreshCounter, offlineLoaded]);

  const can = useCallback(
    (permissionKey: string): boolean => {
      // No root/owner bypass — root and owner resolve through the same
      // pipeline as every member, so the Actions tab matrix governs what
      // they see too.
      const value = state.permissions[permissionKey];
      return value === true || value === 'true';
    },
    [state.permissions],
  );

  const canAction = useCallback(
    (itemType: string, action: OperationsActionKey, workflowKey?: string | null): boolean => {
      return getEffectiveOperationsActionPermission(state.permissions, state.role, itemType, action, workflowKey);
    },
    [state.permissions, state.role],
  );

  const refresh = useCallback(() => {
    const token = authService.getTokens()?.access_token;
    if (token) {
      const cacheKey = getCacheKey(token, moduleKey);
      permissionsCache.delete(cacheKey);
    }
    setRefreshCounter((c) => c + 1);
  }, [moduleKey]);

  return {
    ...state,
    can,
    canAction,
    refresh,
  };
}

// ============================================================================
// CACHE INVALIDATION (for external callers)
// ============================================================================

export function invalidateEffectivePermissionsCache(moduleKey?: string): void {
  if (moduleKey) {
    // Invalidate specific module
    for (const key of permissionsCache.keys()) {
      if (key.endsWith(`:${moduleKey}`)) {
        permissionsCache.delete(key);
      }
    }
  } else {
    permissionsCache.clear();
  }
}

/**
 * Notify all `useEffectivePermissions` hooks that permissions changed and
 * they should re-fetch. Also used by the WebSocket IAM channel handler.
 */
export function notifyEffectivePermissionsChanged(moduleKey?: string): void {
  if (typeof window === 'undefined') return;
  invalidateEffectivePermissionsCache(moduleKey);
  window.dispatchEvent(new CustomEvent(IAM_PERMISSIONS_CHANGED_EVENT, {
    detail: { moduleKey, timestamp: Date.now() },
  }));
}
